import React from 'react';
import PropTypes from 'prop-types';
import { TextField, List, ListItem, Checkbox } from '../atoms';
import { UilAngleDown } from '@iconscout/react-unicons';
import { useDetectOutsideClick } from 'utils/outSideEvent';

const options = [
  { value: 'chocolate', label: 'Chocolate' },
  { value: 'strawberry', label: 'Strawberry' },
  { value: 'vanilla', label: 'Vanilla' },
];

const SelectField = (props) => {
  const {
    height,
    className = 'w-full',
    label = '',
    size = 'small',
    status = '',
    message = '',
    option = options,
    value,
    name,
    onSelect,
    selected,
    onChecked,
    variant = 'secondary',
    type = 'checked-list',
    styles,
    defaultValue = '',
    withArrow = true,
    parentListStyle,
    disabled,
    close,
    anotherList,
    anotherAction,
    customTitleMultiple,
    bolderValue,
    customPadding,
    customList,
    dontSearch = false,
  } = props;

  const dropdownRef = React.useRef(null);
  const [isShow, setIsShow] = useDetectOutsideClick(dropdownRef, false);
  // const [show, setShow] = React.useState(false);
  const [keyword, setKeyword] = React.useState(value);
  const [singleValue, setSingleValue] = React.useState(option);
  const [placeholder, setPlaceholder] = React.useState('Select item');

  const optionOnfilter = React.useMemo(() => {
    if (!keyword) return singleValue;

    return singleValue.filter((opt) => {
      return opt.label.toLowerCase().includes(keyword.toLowerCase());
    });
  }, [keyword, singleValue]);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const refDate = React.useRef(null);

  const handleClickOutside = (event) => {
    if (refDate && !refDate.current.contains(event.target)) {
      setIsShow(false);
    }
  };

  const onClick = () => {
    setKeyword('');
    setIsShow(!isShow);
  };

  const onSelectSingle = (id, value) => {
    onSelect(id, value);
    setKeyword(value);
    setPlaceholder(value);
    setIsShow(false);
  };

  const onSelectMultiple = (id) => {
    onChecked(id);
  };

  React.useEffect(() => {
    setSingleValue(option);
  }, [option]);

  React.useEffect(() => {
    setKeyword(defaultValue);
    setPlaceholder(defaultValue);
  }, [defaultValue]);

  if (type !== 'checked-list') {
    return (
      <div className={`relative ${className}`} ref={refDate}>
        <TextField
          marginTop="mt-0"
          disabled={disabled}
          label={label}
          status={status}
          message={message}
          variant={variant}
          size={size}
          placeholder={placeholder}
          name={name}
          value={keyword || ''}
          onChange={(e) => {
            if (!dontSearch) setKeyword(e.target.value);
          }}
          onClick={() => {
            disabled ? null : setIsShow(true);
            setKeyword('');
          }}
          icon={
            withArrow ? (
              <span onClick={() => (disabled ? null : onClick())}>
                <UilAngleDown />
              </span>
            ) : (
              false
            )
          }
        />

        {isShow ? (
          <div style={styles}>
            <List ref={dropdownRef} style={parentListStyle}>
              <div className={`${optionOnfilter.length > 10 && 'relative max-h-80 h-80 overflow-scroll'}`}>
                {optionOnfilter.map((item, i) => {
                  if (!customList) {
                    return (
                      <ListItem action={() => onSelectSingle(item?.id, item?.value)} key={i}>
                        {item.label}
                      </ListItem>
                    );
                  } else {
                    return (
                      <ListItem action={() => onSelectSingle(item?.id, item?.value)} key={i}>
                        <p className={`${item.color} p-1 rounded-md`}>{item.label}</p>
                      </ListItem>
                    );
                  }
                })}
              </div>
              {anotherList ? (
                <ListItem action={anotherAction} className="flex justify-center bg-green-mint text-neutral-light rounded-b-lg">
                  {anotherList}
                </ListItem>
              ) : (
                ''
              )}
            </List>
          </div>
        ) : (
          ''
        )}
      </div>
    );
  } else {
    return (
      <div className={`relative ${className}`} ref={refDate}>
        <TextField
          marginTop="mt-0"
          customPadding={customPadding}
          bolderValue={bolderValue}
          disabled={disabled}
          label={label}
          status={status}
          message={message}
          variant={variant}
          size={size}
          placeholder={placeholder}
          name={name}
          value={
            customTitleMultiple
              ? customTitleMultiple
              : selected.length >= option.length
              ? 'All selected'
              : `Select items (${selected?.length})`
          }
          onChange={() => setIsShow(true)}
          onClick={() => setIsShow(!isShow)}
          icon={
            withArrow ? (
              <span onClick={onClick}>
                <UilAngleDown />
              </span>
            ) : (
              false
            )
          }
        />

        {isShow && !close ? (
          <div style={styles}>
            <List style={parentListStyle} ref={dropdownRef}>
              <div className={`${height && 'relative max-h-80 h-80 overflow-scroll'}`}>
                {optionOnfilter.map((item, i) => {
                  return (
                    <ListItem key={i}>
                      <Checkbox onChange={() => onSelectMultiple(item?.id)} checked={item?.isChecked} label={item.label} />
                    </ListItem>
                  );
                })}
              </div>
              {anotherList ? (
                <ListItem action={anotherAction} className="flex justify-center bg-green-mint text-neutral-light rounded-b-lg">
                  {anotherList}
                </ListItem>
              ) : (
                ''
              )}
            </List>
          </div>
        ) : (
          ''
        )}
      </div>
    );
  }
};

SelectField.propTypes = {
  value: PropTypes.string || PropTypes.number,
  defaultValue: PropTypes.string || PropTypes.number,
  size: PropTypes.string,
  status: PropTypes.string,
  message: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  dontSearch: PropTypes.bool,
  close: PropTypes.bool,
  bolderValue: PropTypes.bool,
  customList: PropTypes.bool,
  onChecked: PropTypes.func,
  anotherList: PropTypes.string,
  anotherAction: PropTypes.func,
  customTitleMultiple: PropTypes.string,
  customPadding: PropTypes.string,
};

export default SelectField;
